import React, { useState, useEffect, useRef } from "react";
import "./EnterPersonalDetails.css";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import { useDispatch } from "react-redux";
import { setActiveComponent } from "../../../Redux/Slices/activeComponentSlice";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { styled } from "@mui/material/styles";
import dayjs from "dayjs";
import { updateUser } from '../../../api/auth';
import { getToken } from '../../../api/index';
import { setUserDetails } from "../../../Redux/Slices/userDetailsSlice";

const StyledDatePicker = styled(DatePicker)(({ theme }) => ({
  width: '31.51vw',
  '& .MuiInputBase-root': {
    backgroundColor: '#1a1a1a',
    width: '100%',
    height: '52px',
    border: 'none',
    padding: '0px 16px',
    borderRadius: '52px',
    color: 'white',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    boxSizing: 'border-box',
    '&:hover': {
      backgroundColor: '#1a1a1a',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  '& .MuiInputBase-input': {
    padding: '0',
    cursor: 'pointer',
    color: 'white',
    height: '100%',
    '&::placeholder': {
      color: '#7A7A7A',
      opacity: 1,
    },
  },
  '& .MuiIconButton-root': {
    color: '#7A7A7A',
    padding: '8px',
  },
  [theme.breakpoints.down('768')]: {
    width: '100%',
    '& .MuiInputBase-root': {
      height: '48px',
      borderRadius: '12px',
      padding: '0px 16px',
      fontSize: '16px',
    },
  },
  [theme.breakpoints.down('480')]: {
    '& .MuiInputBase-root': {
      height: '44px',
      borderRadius: '10px',
      padding: '0px 14px',
      fontSize: '15px',
    },
  },
  [theme.breakpoints.down('360')]: {
    '& .MuiInputBase-root': {
      height: '40px',
      borderRadius: '8px',
      padding: '0px 12px',
      fontSize: '14px',
    },
  },
}));

function EnterPersonalDetails() {
  const [gender, setGender] = useState("");
  const [date, setDate] = useState("");
  const allLanguages = [
    { label: 'English', value: 'English' },
    { label: 'Japanese', value: 'Japanese' },
    { label: 'Hindi', value: 'Hindi' },
    { label: 'Spanish', value: 'Spanish' },
    { label: 'French', value: 'French' },
    { label: 'German', value: 'German' },
    { label: 'Chinese', value: 'Chinese' },
    { label: 'Russian', value: 'Russian' },
    { label: 'Arabic', value: 'Arabic' },
    { label: 'Portuguese', value: 'Portuguese' },
    { label: 'Italian', value: 'Italian' },
    { label: 'Korean', value: 'Korean' },
    { label: 'Turkish', value: 'Turkish' },
    { label: 'Dutch', value: 'Dutch' },
    { label: 'Swedish', value: 'Swedish' },
    { label: 'Thai', value: 'Thai' },
    { label: 'Greek', value: 'Greek' },
    { label: 'Vietnamese', value: 'Vietnamese' },
    { label: 'Polish', value: 'Polish' },
    { label: 'Bengali', value: 'Bengali' },
  ];
  const allGenders = [
    "Male",
    "Female",
    "Non- Binary",
  ];
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [genderMenuOpen, setGenderMenuOpen] = useState(false);
  const [error, setError] = useState('');
  const dispatch = useDispatch();

  const genderDropdownRef = useRef(null);
  const languageDropdownRef = useRef(null);

  const maxDate = dayjs().subtract(18, 'year'); 
  const minDate = dayjs().subtract(100, 'year');

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideGender = !genderDropdownRef.current || !genderDropdownRef.current.contains(event.target);
      const isOutsideLanguage = !languageDropdownRef.current || !languageDropdownRef.current.contains(event.target);

      if (isOutsideGender && isOutsideLanguage) {
        setGenderMenuOpen(false);
        setLanguageMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleGenderDrowpdownClick = () => {
    setGenderMenuOpen(!genderMenuOpen);
    // Close language dropdown if it's open
    if (languageMenuOpen) {
      setLanguageMenuOpen(false);
    }
  }

  const handleGenderOnChange = (e) => {
    setGender(e);
    setError('');
    setGenderMenuOpen(false);
  };

  const handleLanguageDropdownClick = () => {
    setLanguageMenuOpen(!languageMenuOpen);
    // Close gender dropdown if it's open
    if (genderMenuOpen) {
      setGenderMenuOpen(false);
    }
  };

  const handleLanguageOnChange = (language) => {
    if (selectedLanguages.length === 5) {
      setError("You can only select up to 5 languages");
      setLanguageMenuOpen(false);
      return;
    }
    if (!selectedLanguages.includes(language)) {
      setSelectedLanguages([...selectedLanguages, language]);
    }
    setError('');
    setLanguageMenuOpen(false);
  };
  const removeLanguage = (langToRemove) => {
    setSelectedLanguages(
      selectedLanguages.filter((lang) => lang !== langToRemove)
    );
  };

  const handleNextBtnClick = async () => {
    if (!gender || !date || selectedLanguages.length === 0) {
      setError("All fields are required");
      return;
    }
    try {
      const data = await updateUser({
        userId: localStorage.getItem('userId'),
        gender: gender,
        dateOfBirth: date,
        languages: selectedLanguages,
        formProgress: 'personal details completed',
      }, getToken());
      dispatch(setUserDetails(data.userDetails));
      dispatch(setActiveComponent("enterAddress"));
    } catch (error) {
      console.error("Update User API error:", error);
      setError("Update User Failed");
    }
  };

  return (
    <div className="EnterPersonalDetails">
      <div className="enterPersonalDetailsComponent">
        <span className="signInText">Sign Up</span>
        <div className="rectangleBoxContainer">
          <div className="rectangleBox"></div>
          <div className="rectangleBox coloured"></div>
          <div className="rectangleBox"></div>
        </div>
        <div className="languageContainer">
          <label htmlFor="country" className="labelText">
            Select Gender:
          </label>
          <div ref={genderDropdownRef}>
            <div
              id="gender"
              className="languageSelect"
              onClick={handleGenderDrowpdownClick}
            >
              {!gender ? <span>Select</span> : <span className="inputText">{gender}</span>}
              {genderMenuOpen ? (
                <span
                  className="arrowIcon"
                  sx={{ color: "inherit", height: "12px", width: "12px" }}
                >
                  <KeyboardArrowUpRoundedIcon sx={{ fontSize: { xs: 10, sm: 12, md: 14, lg: 16, xl: 18 } }} />
                </span>
              ) : (
                <span
                  className="arrowIcon"
                  sx={{ color: "inherit", height: "12px", width: "12px" }}
                >
                  <KeyboardArrowDownRoundedIcon sx={{ fontSize: { xs: 10, sm: 12, md: 14, lg: 16, xl: 18 } }} />
                </span>
              )}
            </div>

            {genderMenuOpen && (
              <ul className="dropdownMenu">
                {allGenders.map((gender, index) => (
                  <li
                    key={index}
                    onClick={() => handleGenderOnChange(gender)}
                  >
                    {gender}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="dateContainer">
          <div className="generalInputContainer">
            <label htmlFor="dateInput" className="labelText">
              Date of birth
            </label>
            <div>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <StyledDatePicker
                  value={date ? dayjs(date) : null}
                  onChange={(newValue) => {
                    if (newValue) {
                      setDate(newValue.format('YYYY-MM-DD'));
                      setError('');
                    }
                  }}
                  maxDate={maxDate}
                  minDate={minDate}
                  disableFuture
                  sx={{
                    height: '5vh',
                    backgroundColor: '#1a1a1a',
                    borderRadius: '52px',
                    '& .MuiInputBase-input': {
                      color: 'white',
                      fontSize: 'clamp(12px, 1.5vw, 18px)',
                      padding: 0,
                    },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '52px',
                      backgroundColor: '#1a1a1a',
                      color: 'white',
                      height: '5vh',
                      display: 'flex',
                      alignItems: 'center',
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#aaa',
                      fontSize: 'clamp(12px, 1.3vw, 16px)',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: 'clamp(20px, 2vw, 24px)',
                      color: '#7A7A7A',
                    },
                    '@media (max-width: 768px)': {
                      height: '48px',
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-root': {
                        height: '48px',
                        borderRadius: '12px',
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '16px',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: '20px',
                      },
                    },
                    '@media (max-width: 480px)': {
                      height: '44px',
                      borderRadius: '10px',
                      '& .MuiOutlinedInput-root': {
                        height: '44px',
                        borderRadius: '10px',
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '15px',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: '18px',
                      },
                    },
                    '@media (max-width: 360px)': {
                      height: '40px',
                      borderRadius: '8px',
                      '& .MuiOutlinedInput-root': {
                        height: '40px',
                        borderRadius: '8px',
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '14px',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: '16px',
                      },
                    },
                  }}
                  slotProps={{
                    textField: {
                      placeholder: 'Select Date of Birth',
                      error: Boolean(date && (dayjs(date).isAfter(maxDate) || dayjs(date).isBefore(minDate))),
                      helperText: date && (dayjs(date).isAfter(maxDate) ? 'Must be at least 18 years old' :
                        dayjs(date).isBefore(minDate) ? 'Invalid date of birth' : ''),
                      InputProps: {
                        sx: {
                          color: 'white',
                          backgroundColor: 'transparent',
                          borderRadius: '52px',
                          boxShadow: 'none',
                          height: '5vh',
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: 'clamp(12px, 1.5vw, 16px)',
                          '& input': {
                            color: 'white',
                            padding: '0.75rem 1rem',
                            fontSize: 'clamp(12px, 1.5vw, 16px)',
                            lineHeight: 1.2,
                          },
                          '@media (max-width: 768px)': {
                            height: '48px',
                            borderRadius: '12px',
                            fontSize: '16px',
                            '& input': {
                              fontSize: '16px',
                              padding: '0.5rem 0.75rem',
                            },
                          },
                          '@media (max-width: 480px)': {
                            height: '44px',
                            borderRadius: '10px',
                            fontSize: '15px',
                            '& input': {
                              fontSize: '15px',
                              padding: '0.4rem 0.6rem',
                            },
                          },
                          '@media (max-width: 360px)': {
                            height: '40px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            '& input': {
                              fontSize: '14px',
                              padding: '0.3rem 0.5rem',
                            },
                          },
                        },
                      },
                      InputLabelProps: {
                        sx: {
                          color: '#aaa',
                        },
                      },
                    },
                    popper: {
                      sx: {
                        '& .MuiPaper-root': {
                          backgroundColor: '#1a1a1a',
                          color: 'white',
                          borderRadius: '10px',
                          border: 'none',
                          height: 'auto',
                          width: 'auto',
                          padding: '1rem',
                          boxSizing: 'border-box',
                        },
                        '& .MuiPickersDay-root': {
                          color: 'white',
                          '&:hover': {
                            backgroundColor: '#3a3a3a',
                          },
                          '&.Mui-selected': {
                            backgroundColor: '#3a3a3a',
                            color: 'white',
                          },
                        },
                        '& .MuiDayCalendar-weekDayLabel': {
                          color: 'white',
                        },
                        '& .MuiPickersCalendarHeader-root': {
                          color: 'white',
                        },
                        '& .MuiIconButton-root': {
                          color: 'white',
                        },
                        '& .MuiPickersYear-yearButton': {
                          color: 'white',
                          '&.Mui-selected': {
                            backgroundColor: '#3a3a3a',
                            color: 'white',
                          },
                        },
                        '& .MuiPickersMonth-monthButton': {
                          color: 'white',
                          '&.Mui-selected': {
                            backgroundColor: '#3a3a3a',
                            color: 'white',
                          },
                        },
                      },
                    },

                  }}
                />
              </LocalizationProvider>
            </div>
          </div>
        </div>
        <div className="languageContainer">
          <label htmlFor="language" className="labelText">
            What languages do you speak:
          </label>

          <div ref={languageDropdownRef}>
            <div
              id="language"
              className="languageSelect"
              onClick={handleLanguageDropdownClick}
            >
              {selectedLanguages.length === 0 && <span>Select Languages</span>}
              <div className="selectedTags">
                {selectedLanguages.map((lang) => (
                  <span key={lang} className="tag">
                    {lang}
                    <span
                      className="removeIcon"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeLanguage(lang);
                      }}
                    >
                      <ClearRoundedIcon
                        sx={{ color: "white", height: "12px", width: "12px" }}
                      />
                    </span>
                  </span>
                ))}
              </div>
              {languageMenuOpen ? (
                <span
                  className="arrowIcon"
                  sx={{ color: "inherit", height: "12px", width: "12px" }}
                >
                  <KeyboardArrowUpRoundedIcon sx={{ fontSize: { xs: 10, sm: 12, md: 14, lg: 16, xl: 18 } }} />
                </span>
              ) : (
                <span
                  className="arrowIcon"
                  sx={{ color: "inherit", height: "12px", width: "12px" }}
                >
                  <KeyboardArrowDownRoundedIcon sx={{ fontSize: { xs: 10, sm: 12, md: 14, lg: 16, xl: 18 } }} />
                </span>
              )}
            </div>

            {languageMenuOpen && (
              <ul className="dropdownMenu">
                {allLanguages.map(
                  (lang) =>
                    !selectedLanguages.includes(lang.value) && (
                      <li key={lang.value} onClick={() => handleLanguageOnChange(lang.value)}>
                        {lang.label}
                      </li>
                    )
                )}
              </ul>
            )}
          </div>
          {error && <div className="errorTooltip">{error}</div>}
        </div>
        {/* <div className="NextBtnContainer"> */}
        <button className="btnContainer" onClick={handleNextBtnClick}>
          <span className="btnText">Next</span>
        </button>
        {/* </div> */}
      </div>
    </div>
  );
}

export default EnterPersonalDetails;
